/**
 * Background music: a soundtrack that ships with the game, or anything the player prefers.
 *
 * Three sources. The **tracklist** is the game's own soundtrack, listed in
 * `public/sounds/soundtrack.json` and fetched at runtime rather than compiled in — adding a
 * track is dropping a file in `public/sounds/` and adding a line to that manifest, with no
 * rebuild and no server restart. The other two are the player's: a local file off their own
 * disk (File API, `URL.createObjectURL`, never leaves the browser) or a URL pasted in.
 *
 * The tracklist plays **only on the game screen** — the grid, the race, the results — and
 * not in the lobby. A soundtrack is race music; a lobby is where people are typing names
 * and reading a map at each other. A file or URL the player deliberately chose is not gated
 * that way, because choosing one is a clear enough statement that they want to hear it.
 *
 * It shares `audio.ts`'s first rule: it is allowed to fail. A file that will not decode, a
 * URL that 404s or CORS-blocks, a browser with no `<audio>` element — every one of those
 * has to leave silence behind, with a status the settings panel can show and nothing on
 * the console.
 *
 * It deliberately does **not** share `audio.ts`'s `AudioContext` graph. A pasted URL is
 * somebody else's server; the overwhelming majority will not answer with the CORS headers
 * `fetch` + `decodeAudioData` need, and a `MediaElementAudioSourceNode` taints the same
 * way. Plain `<audio>` playback never needed CORS in the first place — only reading the
 * samples back out would have — so it is both the simpler graph and the one that actually
 * works against an arbitrary link. The cost is losing precise Web Audio scheduling, which
 * this does not need: the only timing constraint on music is "get out of the way of the
 * start signal", not "land on a sample".
 */

import { t } from './i18n/i18n.js';

const VOLUME_KEY = 'osm-racer.music.volume';
const URL_KEY = 'osm-racer.music.url';
const SOURCE_KEY = 'osm-racer.music.source';

/** Where the soundtrack manifest lives. Missing or unparseable means no bundled tracks and
 *  the rest of the feature behaves exactly as it did before there were any. */
const CATALOGUE_URL = '/sounds/soundtrack.json';
/** Relative `src` entries in the manifest hang off here, so a line is a filename. */
const CATALOGUE_BASE = '/sounds/';

/**
 * Quiet by default, and much quieter than it looks.
 *
 * This is a bed under an engine, seven other engines, tyre noise and a countdown — the
 * things the player actually needs to hear to drive. At 0.5 the soundtrack won the mix
 * against all of them. The manifest can override it, and anyone who moves the slider keeps
 * their own setting forever; this is only the number a first-time player starts on.
 */
const DEFAULT_VOLUME = 0.08;

/**
 * How far the countdown ducks music, as a fraction of the player's own volume. Not zero:
 * a hard cut announces the countdown as loudly as raising the beeps would, and this is
 * meant to fade into the background rather than announce itself. 0.12 is roughly -18 dB —
 * quiet enough that a beep normalised to the same -16 LUFS as everything else (see
 * `audio.ts`, DECISIONS §17/§23) sits clearly on top of it.
 */
const DUCK_DEPTH = 0.12;
/** Fast enough to be fully ducked before the countdown's first beep in the worst case —
 *  `playCountdown` can fire with as little as a few hundred ms of warning if a countdown
 *  message arrives late (see `audio.ts`). */
const DUCK_MS = 220;
/** Slower coming back — the green light already has the ignition and the engine note to
 *  carry it; music does not need to leap back in over them. */
const UNDUCK_MS = 900;

export type MusicSource = 'none' | 'tracklist' | 'file' | 'url';

export interface Track {
  title: string;
  src: string;
}

/** Read by the same Playwright suite that reads `AudioDebug` — see `getAudio` in `main.ts`. */
export interface MusicDebug {
  source: MusicSource;
  /** Null until something has been loaded at all. */
  label: string | null;
  playing: boolean;
  /** How many tracks the manifest offered. Zero when there is no soundtrack installed. */
  trackCount: number;
  /** True while the game screen is up, which is what the tracklist follows. */
  inRace: boolean;
  /** The player's own slider position, 0..1 — independent of ducking and of mute. */
  volume: number;
  /** 1 at rest, `DUCK_DEPTH` during a countdown, ramping between the two. */
  duckMultiplier: number;
  muted: boolean;
  error: string | null;
}

class MusicPlayer {
  private el: HTMLAudioElement | null = null;
  private objectUrl: string | null = null;
  private source: MusicSource = 'none';
  private label: string | null = null;
  private volume = readStoredVolume();
  private muted = false;
  private duckMultiplier = 1;
  private duckToken = 0;
  private error: string | null = null;
  private tracks: Track[] = [];
  private trackIndex = 0;
  private inRace = false;

  /** The last URL the player typed, restored into the field even though playback itself
   *  is never resumed automatically — see the note on `loadUrl`. */
  get storedUrl(): string {
    return readStoredUrl();
  }

  get currentVolume(): number {
    return this.volume;
  }

  setVolume(v: number): void {
    this.volume = clamp01(v);
    try {
      localStorage.setItem(VOLUME_KEY, String(this.volume));
    } catch {
      // Private browsing. The setting simply will not persist.
    }
    this.apply();
  }

  /** Driven by `carAudio`'s mute, so `M` covers music without the settings panel having
   *  to know anything about the keyboard. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    this.apply();
  }

  get isMuted(): boolean {
    return this.muted;
  }

  // -------------------------------------------------------------------------
  // the shipped soundtrack
  // -------------------------------------------------------------------------

  /**
   * Reads the manifest. Called once at startup; never throws, and a failure just means the
   * soundtrack option is not offered.
   *
   * The manifest's `volume` is applied only to a player who has never touched the slider.
   * Overriding a stored preference from a config file would mean the game quietly turning
   * its own music back up on somebody who had turned it down.
   */
  async loadCatalogue(): Promise<void> {
    try {
      const response = await fetch(CATALOGUE_URL, { cache: 'no-cache' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body = (await response.json()) as { volume?: unknown; tracks?: unknown };
      this.tracks = Array.isArray(body.tracks)
        ? (body.tracks as Track[])
            .filter((track) => track && typeof track.src === 'string' && track.src.trim())
            .map((track, i) => ({ title: String(track.title ?? t('Track {n}', { n: i + 1 })), src: resolveTrack(track.src) }))
        : [];
      if (hasStoredVolume() === false && Number.isFinite(body.volume)) {
        this.volume = clamp01(Number(body.volume));
      }
    } catch {
      // No soundtrack installed, or the manifest is malformed. Both are "no bundled tracks",
      // which is exactly what this feature looked like before there were any.
      this.tracks = [];
    }

    // A first-time player gets the soundtrack, and so does a returning one who had it
    // selected. Both, not just the first: keying this on "nothing stored yet" meant the
    // soundtrack played once and then never again, because selecting it is itself what
    // wrote the setting that stopped it being restored.
    //
    // `file` and `url` are deliberately not restored. A file lives behind a file picker that
    // only a fresh gesture can open, and a pasted URL is somebody else's server that should
    // not start playing the instant a shared lobby link is opened — see `loadUrl`.
    const stored = readStoredSource();
    if (this.tracks.length && this.source === 'none' && (stored === null || stored === 'tracklist')) {
      this.useTracklist();
    }
  }

  get catalogue(): readonly Track[] {
    return this.tracks;
  }

  /** Selects the shipped soundtrack. It only actually sounds once a race screen is up. */
  useTracklist(): void {
    if (!this.tracks.length) {
      this.error = t('No soundtrack is installed on this server.');
      return;
    }
    this.releaseObjectUrl();
    this.source = 'tracklist';
    this.error = null;
    writeStoredSource('tracklist');
    this.trackIndex = this.trackIndex % this.tracks.length;
    this.applyTrack();
  }

  /**
   * The game screen came up, or went away.
   *
   * Only the tracklist follows this — see the module comment. `play()` here is allowed
   * because getting to a race always follows a click, so the autoplay policy is satisfied
   * by the time it matters.
   */
  setInRace(active: boolean): void {
    if (this.inRace === active) return;
    this.inRace = active;
    if (this.source !== 'tracklist') return;
    if (active) this.applyTrack();
    else this.el?.pause();
  }

  private applyTrack(): void {
    const track = this.tracks[this.trackIndex];
    const el = this.ensureElement();
    if (!el || !track) return;
    this.label = track.title;
    // One track loops; several advance and wrap. `ended` never fires on a looping element,
    // so the two cannot both be left on.
    el.loop = this.tracks.length === 1;
    if (el.src !== new URL(track.src, location.href).href) el.src = track.src;
    this.apply();
    if (!this.inRace) {
      el.pause();
      return;
    }
    void el.play().catch(() => {
      // Autoplay refused, or a codec this browser will not decode. Silence, and a status
      // the panel can show — never a console error.
      this.error = t('The soundtrack would not start. Press play in the audio settings.');
    });
  }

  private advanceTrack(): void {
    if (this.source !== 'tracklist' || this.tracks.length < 2) return;
    this.trackIndex = (this.trackIndex + 1) % this.tracks.length;
    this.applyTrack();
  }

  loadFile(file: File): void {
    const el = this.ensureElement();
    if (!el) return;
    this.releaseObjectUrl();
    try {
      const url = URL.createObjectURL(file);
      this.objectUrl = url;
      this.source = 'file';
      el.loop = true;
      writeStoredSource('file');
      this.label = file.name;
      this.error = null;
      el.src = url;
      this.apply();
      void el.play().catch(() => {
        // A file picked from an <input type=file change> handler is itself a user
        // gesture, so this should not fire in practice — but a codec the browser
        // will not decode is a real failure mode, and it is silence, not a console error.
        this.error = t('Could not play that file — the browser may not support its format.');
      });
    } catch {
      this.error = t('Could not read that file.');
    }
  }

  /**
   * Loads a pasted URL and remembers it for next time — but does not replay it
   * automatically on a later visit. Autoplay policy would block it anyway without a fresh
   * gesture, and starting a stranger's URL playing the moment a page loads is exactly the
   * surprise-noise problem this whole feature defaults to "off" to avoid. Restoring the
   * text into the field (`storedUrl`) so one click gets it going again is the compromise.
   */
  loadUrl(url: string): void {
    const trimmed = url.trim();
    if (!trimmed) return;
    const el = this.ensureElement();
    if (!el) return;
    this.releaseObjectUrl();
    try {
      this.source = 'url';
      el.loop = true;
      writeStoredSource('url');
      this.label = trimmed;
      this.error = null;
      el.src = trimmed;
      try {
        localStorage.setItem(URL_KEY, trimmed);
      } catch {
        // Private browsing.
      }
      this.apply();
      void el.play().catch(() => {
        this.error = t('That URL would not play — check it allows cross-site playback.');
      });
    } catch {
      this.error = t('Could not load that URL.');
    }
  }

  stop(): void {
    this.source = 'none';
    writeStoredSource('none');
    this.label = null;
    this.error = null;
    if (this.el) {
      try {
        this.el.pause();
        this.el.removeAttribute('src');
        this.el.load();
      } catch {
        // Nothing left to stop.
      }
    }
    this.releaseObjectUrl();
  }

  /** Ducks under the start signal. See the module comment for why this is a hand-rolled
   *  ramp rather than a Web Audio automation: there is no AudioParam on a media element. */
  duck(): void {
    this.rampTo(DUCK_DEPTH, DUCK_MS);
  }

  unduck(): void {
    this.rampTo(1, UNDUCK_MS);
  }

  debug(): MusicDebug {
    return {
      source: this.source,
      label: this.label,
      playing: this.el !== null && !this.el.paused && !this.el.ended,
      trackCount: this.tracks.length,
      inRace: this.inRace,
      volume: this.volume,
      duckMultiplier: this.duckMultiplier,
      muted: this.muted,
      error: this.error,
    };
  }

  // -------------------------------------------------------------------------
  // internals
  // -------------------------------------------------------------------------

  private ensureElement(): HTMLAudioElement | null {
    if (this.el) return this.el;
    try {
      const el = new Audio();
      el.loop = true;
      el.preload = 'auto';
      el.addEventListener('error', () => {
        this.error = t('That track would not play.');
      });
      el.addEventListener('ended', () => this.advanceTrack());
      this.el = el;
      return el;
    } catch {
      return null;
    }
  }

  private releaseObjectUrl(): void {
    if (this.objectUrl) {
      try {
        URL.revokeObjectURL(this.objectUrl);
      } catch {
        // Already revoked, or never valid.
      }
      this.objectUrl = null;
    }
  }

  private rampTo(target: number, ms: number): void {
    if (typeof requestAnimationFrame !== 'function') {
      this.duckMultiplier = target;
      this.apply();
      return;
    }
    const token = ++this.duckToken;
    const start = this.duckMultiplier;
    const startedAt = performance.now();
    const step = () => {
      if (token !== this.duckToken) return;
      const t = ms > 0 ? Math.min(1, (performance.now() - startedAt) / ms) : 1;
      this.duckMultiplier = start + (target - start) * t;
      this.apply();
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  private apply(): void {
    if (!this.el) return;
    try {
      this.el.volume = this.muted ? 0 : clamp01(this.volume * this.duckMultiplier);
    } catch {
      // Some embedders (rare) throw on setting .volume; nothing to do about it.
    }
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function readStoredVolume(): number {
  try {
    const raw = localStorage.getItem(VOLUME_KEY);
    if (raw === null) return DEFAULT_VOLUME;
    const v = Number(raw);
    return Number.isFinite(v) ? clamp01(v) : DEFAULT_VOLUME;
  } catch {
    return DEFAULT_VOLUME;
  }
}

/**
 * Whether the player has ever moved the volume slider.
 *
 * Distinct from "what is the volume", because the manifest's default may only override a
 * value nobody has set. `readStoredVolume` cannot answer this — it returns a number either
 * way — and conflating the two is how a config file ends up turning music back up on
 * somebody who turned it down.
 */
function hasStoredVolume(): boolean {
  try {
    return localStorage.getItem(VOLUME_KEY) !== null;
  } catch {
    return false;
  }
}

function readStoredSource(): string | null {
  try {
    return localStorage.getItem(SOURCE_KEY);
  } catch {
    return null;
  }
}

function writeStoredSource(source: MusicSource): void {
  try {
    localStorage.setItem(SOURCE_KEY, source);
  } catch {
    // Private browsing.
  }
}

export function resolveTrack(src: string): string {
  const trimmed = src.trim();
  return /^(https?:)?\/\//.test(trimmed) || trimmed.startsWith('/')
    ? trimmed
    : CATALOGUE_BASE + trimmed;
}

function readStoredUrl(): string {
  try {
    return localStorage.getItem(URL_KEY) ?? '';
  } catch {
    return '';
  }
}

/** One instance for the page, same reasoning as `carAudio`: a `Game` is torn down and
 *  rebuilt per region, and music should not care. */
export const musicPlayer = new MusicPlayer();
